/** Inline SVG line charts as strings: no chart library, pure, testable. */

export interface Series {
  label: string;
  color: string;
  dashed?: boolean;
  points: Array<{ x: number; y: number }>;
}

export interface ChartOptions {
  width?: number;
  height?: number;
  yLabel?: string;
  xTicks?: Array<{ x: number; label: string }>;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

export function lineChartSvg(series: readonly Series[], opts: ChartOptions = {}): string {
  const width = opts.width ?? 360;
  const height = opts.height ?? 180;
  const pad = { l: 40, r: 12, t: 12, b: 28 };
  const pts = series.flatMap((s) => s.points);
  if (pts.length === 0) {
    return `<svg class="chart" viewBox="0 0 ${width} ${height}" role="img"><text x="${width / 2}" y="${height / 2}" text-anchor="middle" class="chart-empty">—</text></svg>`;
  }
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  let x0 = Math.min(...xs);
  let x1 = Math.max(...xs);
  if (x0 === x1) {
    x0 -= 1;
    x1 += 1;
  }
  const y0 = 0;
  const y1 = Math.max(...ys, 1) * 1.1;
  const sx = (x: number) => pad.l + ((x - x0) / (x1 - x0)) * (width - pad.l - pad.r);
  const sy = (y: number) => height - pad.b - ((y - y0) / (y1 - y0)) * (height - pad.t - pad.b);

  const yTicks = 4;
  const grid: string[] = [];
  for (let i = 0; i <= yTicks; i++) {
    const v = (y1 / yTicks) * i;
    const y = sy(v);
    grid.push(`<line x1="${pad.l}" x2="${width - pad.r}" y1="${y.toFixed(1)}" y2="${y.toFixed(1)}" class="chart-grid"/>`);
    grid.push(`<text x="${pad.l - 6}" y="${(y + 4).toFixed(1)}" text-anchor="end" class="chart-tick">${v >= 10 ? Math.round(v) : v.toFixed(1)}</text>`);
  }
  const xTicks = (opts.xTicks ?? []).map((tk) => `<text x="${sx(tk.x).toFixed(1)}" y="${height - 8}" text-anchor="middle" class="chart-tick">${esc(tk.label)}</text>`);

  const lines = series.map((s) => {
    const sorted = s.points.slice().sort((a, b) => a.x - b.x);
    const d = sorted.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ');
    const dots = sorted.map((p) => `<circle cx="${sx(p.x).toFixed(1)}" cy="${sy(p.y).toFixed(1)}" r="3.5" fill="${s.color}"><title>${esc(s.label)}: ${p.y}</title></circle>`).join('');
    return `<path d="${d}" fill="none" stroke="${s.color}" stroke-width="2"${s.dashed ? ' stroke-dasharray="5 4"' : ''}/>${dots}`;
  });

  const legend = series
    .map((s, i) => `<g transform="translate(${pad.l + i * 120},${pad.t})"><line x1="0" x2="18" y1="6" y2="6" stroke="${s.color}" stroke-width="2"${s.dashed ? ' stroke-dasharray="5 4"' : ''}/><text x="22" y="10" class="chart-tick">${esc(s.label)}</text></g>`)
    .join('');

  const yLabel = opts.yLabel ? `<text x="4" y="${pad.t + 10}" class="chart-tick">${esc(opts.yLabel)}</text>` : '';
  return `<svg class="chart" viewBox="0 0 ${width} ${height}" role="img">${grid.join('')}${xTicks.join('')}${lines.join('')}${legend}${yLabel}</svg>`;
}

/** Evenly spaced date ticks (first, middle, last) from ISO dates. */
export function dateTicks(dates: readonly string[]): Array<{ x: number; label: string }> {
  const uniq = [...new Set(dates)].sort();
  if (uniq.length === 0) return [];
  const pick = uniq.length <= 3 ? uniq : [uniq[0], uniq[Math.floor(uniq.length / 2)], uniq[uniq.length - 1]];
  return pick.map((d) => ({ x: dateX(d), label: d.slice(0, 7) }));
}

export function dateX(iso: string): number {
  return Date.parse(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
}
